import { AxiosInstance } from 'axios'
import { SortOrder } from '../../components/SortSelector'
import { Chatflow } from '../../types/chatflow'
import z from 'zod'
import { InaccessibleResourcesResponse, ShareChatflowResponse } from '../generated/internal/internalApi'

export const chatflowUserResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
})

export const nodeInfoSchema = z.object({
  chatflowId: z.string(),
  chatflowName: z.string(),
  nodeId: z.string(),
  path: z.array(z.string()),
})

export const chatflowResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  flowData: z.string(),
  allowAnonymousAccess: z.boolean(),
  apiConfig: z.string(),
  createdDate: z.string(),
  updatedDate: z.string(),
  userId: z.string().nullable(),
  fileNamesJson: z.string().nullable(),
  chatFlowUsers: z.array(chatflowUserResponseSchema).optional(),
  chatFlowType: z.enum(['agent', 'pipeline', 'playground']).nullable(),
  files: z.array(z.object({ name: z.string(), url: z.string() })).optional(),
  supportsVision: z.array(nodeInfoSchema).optional(),
  supportsCodeInterpreter: z.array(nodeInfoSchema).optional(),
  description: z.string().nullable(),
  viewerPermissionLevel: z.enum(['use', 'useAndView', 'fullControl']).optional(),
  canViewAllSessions: z.boolean().optional(),
  canDelete: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  // Built-in Teammates are read-only: the server refuses every write route on them. (WF-2616)
  isSystem: z.boolean().optional(),
  systemKey: z.string().nullable().optional(),
  // The Teammate's current revision number, sent back as If-Match on the next full save. (WF-2795)
  revisionNo: z.number().optional(),
})

export type ChatflowResponse = z.infer<typeof chatflowResponseSchema>

export const chatflowsResponseSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    flowData: z.string(),
    description: z.string().nullable(),
  })
)

export const chatflowListResponseSchema = z.object({
  pageSize: z.number(),
  pageNumber: z.number(),
  numResults: z.number(),
  chatflows: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      flowData: z.string(),
      description: z.string().nullable(),
      canDelete: z.boolean().optional(),
      // This schema is narrow and zod strips unknown keys, so a field that is not declared here is
      // silently dropped from every list row. isSystem has to be declared for the card menus to be
      // able to gate on it at all. `systemKey` is deliberately absent: the server's list schema
      // strips it, so it is never populated on a list row. (WF-2616)
      isSystem: z.boolean().optional(),
    })
  ),
})

export type ChatflowListResponse = z.infer<typeof chatflowListResponseSchema>

type GetChatflowsParams = {
  chatFlowType?: string
  filter?: string
  sort?: string
  pageSize?: number
  pageNumber?: number
  orderBy?: 'name' | 'createdDate' | 'updatedDate'
  orderDirection?: 'ASC' | 'DESC'
  show?: 'owned' | 'shared' | 'accessible'
}

export default class ChatflowsApi {
  constructor(private client: AxiosInstance) {}

  getChatflows = (chatFlowType?: string, filter?: string, sort?: SortOrder<keyof Chatflow>[]) => {
    const params: GetChatflowsParams = {}
    if (chatFlowType) {
      params.chatFlowType = chatFlowType
    }
    if (filter?.trim()) {
      params.filter = filter.trim()
    }
    if (sort?.length) {
      params.sort = sort.map(s => s.toString()).join(',')
    }
    return this.client.get(`/chatflows`, { params })
  }

  getChatflowsPaginated = (options: GetChatflowsParams) => {
    const params: GetChatflowsParams = {
      pageSize: options.pageSize,
      pageNumber: options.pageNumber,
    }

    if (options.chatFlowType) {
      params.chatFlowType = options.chatFlowType
    }

    if (options.filter?.trim()) {
      params.filter = options.filter.trim()
    }

    if (options.orderBy) {
      params.orderBy = options.orderBy
    }

    if (options.orderDirection) {
      params.orderDirection = options.orderDirection
    }

    if (options.show) {
      params.show = options.show
    }

    return this.client.get(`/chatflows`, { params })
  }

  getSpecificChatflow = (id: string) => this.client.get(`/chatflows/${id}`)

  createNewChatflow = (formData: any) =>
    this.client.post(`/chatflows`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

  updateChatflow = (id: string, formData: any) =>
    this.client.put(`/chatflows/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

  attachToMessage = (id: string, formData: any) =>
    this.client.post(`/chatflows/${id}/attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

  removeAttachments = (id: string, attachmentIds: string[]) => {
    return this.client.delete(`/chatflows/${id}/attachment`, { data: { attachmentIds } })
  }

  getMessageAttachments = (id: string) => this.client.get(`/chatflows/${id}/attachment`)

  deleteChatflow = id => this.client.delete(`/chatflows/${id}`)

  getAssociatedTeams = (id: string) => this.client.get<{ teams: Array<{ id: string; name: string }> }>(`/chatflows/${id}/associated-teams`)

  /** The Skills, Tools and nodes in this teammate the current viewer cannot access. (WF-2727) */
  getInaccessibleResources = (id: string) => this.client.get<InaccessibleResourcesResponse>(`/chatflows/${id}/inaccessible-resources`)

  duplicateChatflow = (id: string) => this.client.post<ChatflowResponse>(`/chatflows/${id}/duplicate`)

  /** Promotes a playground teammate to a standard teammate. */
  convertToTeammate = (id: string) => this.client.post<ChatflowResponse>(`/chatflows/${id}/convert-to-teammate`)

  shareChatflow = (id, body) => this.client.post<ShareChatflowResponse>(`/chatflows/${id}/share`, body)

  unshareChatflow = (id, body) => this.client.post(`/chatflows/${id}/unshare`, body)

  getSharedUsers = id => this.client.get(`/chatflows/${id}/users`)

  getSharedGroups = id => this.client.get(`/chatflows/${id}/groups`)

  getIsChatflowStreaming = id => this.client.get(`/chatflows/${id}/streamable`)
}
